import { Text } from '@thanx/uikit/text'
import { buildTranslate } from 'locales'
import React from 'react'
import { useStyletron } from 'styletron-react'
import { merchantTheme } from 'theme'

const t = buildTranslate('recovery_ai.escalation_notification_scope')

const CALLOUT_GAP_PX = 12
const CALLOUT_PADDING_PX = 24
const CALLOUT_BORDER_RADIUS_PX = 4
const INFO_ICON_SIZE_PX = 16
const INFO_ICON_TOP_OFFSET_PX = 2

type Props = {
  /** Overrides the "every location" line. Omit to keep the RecoveryAI-branded default. */
  allLocationsCopy?: string
  /** Overrides the "handled automatically" line. Omit to keep the RecoveryAI-branded default. */
  aiHandledCopy?: string
}

// Shared grey notification-scope callout — one source consumed by both the
// Settings escalation-contacts pane and the wizard escalations step, so the
// scope explainer can't drift between surfaces. The two lines sit in SEPARATE
// block wrappers because Text.BodyText3 renders an inline <span>; two adjacent
// spans would re-wrap together, destroying the design's deliberate hard break.
const EscalationNotificationScopeNote = ({
  allLocationsCopy,
  aiHandledCopy,
}: Props): React.ReactElement => {
  const [css] = useStyletron()

  return (
    <div
      className={`d-flex align-items-start ${css({
        gap: `${CALLOUT_GAP_PX}px`,
        padding: `${CALLOUT_PADDING_PX}px`,
        backgroundColor: merchantTheme.colors.grey10,
        borderRadius: `${CALLOUT_BORDER_RADIUS_PX}px`,
      })}`}
    >
      {/* Decorative — the adjacent text conveys the meaning, so the icon is
          hidden from assistive tech (CLAUDE.md a11y rule). */}
      <i
        className={`fa fa-info-circle ${css({
          fontSize: `${INFO_ICON_SIZE_PX}px`,
          color: merchantTheme.colors.grey90,
          flexShrink: 0,
          marginTop: `${INFO_ICON_TOP_OFFSET_PX}px`,
        })}`}
        aria-hidden="true"
      />
      {/* grey90, not the design's grey70: grey70 on the grey10 band is 4.31:1,
          below WCAG AA 4.5:1 for body text, and there is no grey80 token. */}
      <div className="d-flex flex-column">
        <div>
          <Text.BodyText3 color="grey90">
            {allLocationsCopy ?? t('all_locations')}
          </Text.BodyText3>
        </div>
        <div>
          <Text.BodyText3 color="grey90">
            {aiHandledCopy ?? t('ai_handled')}
          </Text.BodyText3>
        </div>
      </div>
    </div>
  )
}

export default EscalationNotificationScopeNote
